import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AXES,
  DOF_RANGES,
  POINT_RANGE,
  asymAxes,
  buildSkeleton,
  dofEdit,
  isAsym,
  pointFor,
  type AxisKey,
  type DofKey,
  type HumanoidModel,
  type PointKey,
  type Skeleton,
} from '@/domain/hs1'
import {
  MECHANISM_LABELS,
  axes as rigAxes,
  chain,
  childrenOf,
  mechanisms,
  ratioAt,
  ratioLabel,
  rigFromModel,
  trackFor,
  segmentLength as branchLength,
  worldPoints,
  outlinePath,
  parts,
  partsOn,
  PROVENANCE_LABELS,
  type Part,
  type Ratio,
  type RigNode,
} from '@/domain/rig'
import { Badge, Button, Card, Switch, Table, Td, Th } from '@/components/ui'
import { CycleChart } from '@/sim/CycleChart'
import { StickFigure } from '@/sim/StickFigure'
import { useSimulation } from '@/sim/SimulationProvider'
import { NumberCell, num } from '@/sim/parts'
import { usePointEditor, type PointEditor } from './SideAsk'

/**
 * Achsbaum — das Strichmännchen als Bedienoberfläche.
 *
 * Ein Klick in eine der vier Ansichten wählt einen Knickpunkt; darunter stehen
 * seine Werte. Die Tabelle bleibt daneben stehen, weil sie etwas anderes kann
 * als das Bild: sechzehn Zeilen nebeneinander vergleichen.
 *
 * Die Koordinaten sind hier absolut, vom Kreuzgelenk aus — so, wie der
 * Punktsatz sie führt. Der Abstand zum Elternknoten steht daneben und wird
 * gerechnet.
 */

const POINT_ORDER: PointKey[] = [
  'cross',
  'waistD',
  'waistC',
  'chest',
  'pelvis',
  'hipG',
  'hipR',
  'hipN',
  'knee',
  'ankle',
  'mtp',
  'sole',
  'shoulder',
  'elbow',
  'wrist',
  'headC',
]

const KIND_LABEL: Record<RigNode['kind'], string> = {
  gelenk: 'Gelenk',
  anschluss: 'Anschluss',
  mechanik: 'Mechanik',
}

const STATE_LABEL: Record<RigNode['state'], string> = {
  entwurf: 'Entwurf',
  ausgelegt: 'ausgelegt',
  konstruiert: 'konstruiert',
  fest: 'festgeschrieben',
}

export function Axes() {
  const { base, model, state } = useSimulation()
  const editor = usePointEditor()
  const [selected, setSelected] = useState<string | null>('knee')
  const [freedoms, setFreedoms] = useState(true)
  const [bothSides, setBothSides] = useState(true)
  const [large, setLarge] = useState(false)

  const rig = useMemo(() => rigFromModel(model, state.off), [model, state.off])
  const axisList = useMemo(() => rigAxes(rig), [rig])
  const mechs = useMemo(() => mechanisms(rig), [rig])

  const node = selected ? (rig.byId[selected] ?? null) : null

  return (
    <div className="stack">
      <Card
        flush
        title="Knickpunkte in vier Ansichten"
        eyebrow={`${rig.nodes.length} Punkte · ${axisList.length} Achsen · ${mechs.length} Baugruppen`}
        actions={
          <div className="axes__switches">
            <Switch label="Freiheiten" checked={freedoms} onChange={(e) => setFreedoms(e.currentTarget.checked)} />
            <Switch label="Beide Seiten" checked={bothSides} onChange={(e) => setBothSides(e.currentTarget.checked)} />
            <Switch label="Gross" checked={large} onChange={(e) => setLarge(e.currentTarget.checked)} />
          </div>
        }
      >
        <div className="axes__figure">
          <StickFigure
            rig={rig}
            selected={selected}
            onSelect={setSelected}
            freedoms={freedoms}
            bothSides={bothSides}
            large={large}
          />
        </div>
        <p className="page-lead axes__hint">
          Ein Klick wählt einen Punkt. Die Kreissektoren zeigen die Winkelfreiheit, aber nur dort,
          wo die Drehachse senkrecht auf der Bildebene steht. Gefüllte Kreise sind Gelenke, offene
          Anschlusspunkte, die kleinen die Mechanik des Rumpfaktuators.
        </p>
      </Card>

      {node && <Inspector node={node} editor={editor} />}

      <Card flush title="Alle Punkte">
        <p className="page-lead" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
          Nullpunkt ist das grosse Kreuzgelenk des Rumpfaktuators. x nach vorn zur Bauchseite, y nach
          aussen zur linken Körperseite, z nach oben zum Kopf.
        </p>
        <Table>
          <thead>
            <tr>
              <Th>Punkt</Th>
              <Th>Art</Th>
              <Th>hängt an</Th>
              <Th numeric>x</Th>
              <Th numeric>y</Th>
              <Th numeric>z</Th>
              <Th numeric>Abstand</Th>
              <Th>Winkelfreiheit</Th>
            </tr>
          </thead>
          <tbody>
            {POINT_ORDER.flatMap((key) => {
              const point = model.points[key]
              const entry = rig.byId[key]
              const asym = isAsym(model, key)
              const rows = [
                <tr
                  key={key}
                  className={selected === key ? 'axis-row axis-row--on' : 'axis-row'}
                  onClick={() => setSelected(key)}
                >
                  <Td>
                    {point.label}
                    {point.fixed && <span className="cell-muted"> · Nullpunkt</span>}
                    {asym && (
                      <>
                        {' '}
                        <Badge tone="warning">unsymmetrisch</Badge>
                      </>
                    )}
                  </Td>
                  <Td>
                    <span className="cell-muted">{entry ? KIND_LABEL[entry.kind] : '—'}</span>
                  </Td>
                  <Td>
                    <span className="cell-muted">
                      {asym ? 'rechts' : (entry?.parent ? model.points[entry.parent as PointKey].label : '—')}
                    </span>
                  </Td>
                  {(['x', 'y', 'z'] as const).map((axis) => (
                    <Td key={axis} numeric>
                      <NumberCell
                        value={point[axis]}
                        base={base.points[key][axis]}
                        min={POINT_RANGE.min}
                        max={POINT_RANGE.max}
                        step={POINT_RANGE.step}
                        digits={POINT_RANGE.digits}
                        readOnly={point.fixed}
                        onChange={(next) => editor.change(key, axis, next)}
                      />
                    </Td>
                  ))}
                  <Td numeric>
                    {entry?.parent ? (
                      `${num(branchLength(rig, key), 0)} mm`
                    ) : (
                      <span className="cell-muted">—</span>
                    )}
                  </Td>
                  <Td>
                    {point.dof ? model.dofs[point.dof].label : <span className="cell-muted">—</span>}
                  </Td>
                </tr>,
              ]

              /* Die zweite Zeile gibt es nur bei gewollter Unsymmetrie. Sie
                 immer zu zeigen hiesse, jede Zeile zu verdoppeln, um an
                 zwölf von sechzehn Punkten zweimal dieselbe Zahl zu
                 schreiben. */
              if (asym) {
                const own = asymAxes(model, key)
                rows.push(
                  <tr
                    key={`${key}-L`}
                    className={selected === key ? 'axis-row axis-row--on asym-row' : 'axis-row asym-row'}
                    onClick={() => setSelected(key)}
                  >
                    <Td />
                    <Td />
                    <Td>
                      <span className="cell-muted">links</span>
                    </Td>
                    {(['x', 'y', 'z'] as const).map((axis) => (
                      <Td key={axis} numeric>
                        <NumberCell
                          value={pointFor(model, key, 'L')[axis]}
                          base={point[axis]}
                          min={POINT_RANGE.min}
                          max={POINT_RANGE.max}
                          step={POINT_RANGE.step}
                          digits={POINT_RANGE.digits}
                          onChange={(next) => editor.changeLeft(key, axis, next)}
                        />
                      </Td>
                    ))}
                    <Td numeric>
                      <span className="cell-muted">
                        {entry?.atLeft ? `${num(Math.hypot(entry.atLeft.x, entry.atLeft.y, entry.atLeft.z), 0)} mm` : '—'}
                      </span>
                    </Td>
                    <Td>
                      <span className="cell-muted">{own.join(', ')} abweichend</span>
                    </Td>
                  </tr>,
                )
              }
              return rows
            })}
          </tbody>
        </Table>
      </Card>

      {editor.dialog}
    </div>
  )
}

/* -------------------------------------------------------------------------
   Der gewählte Punkt
   ------------------------------------------------------------------------- */

function Inspector({ node, editor }: { node: RigNode; editor: PointEditor }) {
  const { base, model, state, setEdit, setOff } = useSimulation()
  const rig = useMemo(() => rigFromModel(model, state.off), [model, state.off])
  const world = useMemo(() => worldPoints(rig), [rig])
  const skeleton = useMemo(() => buildSkeleton(model), [model])

  const key = node.id as PointKey
  const point = model.points[key]
  const dofKey = point.dof as DofKey | null
  const dof = dofKey ? model.dofs[dofKey] : null
  const off = node.off
  const asym = isAsym(model, key)
  const own = asymAxes(model, key)
  /* Die Baugruppe dieses Gelenks — Ebene 2. Sie sagt, ob die Achse einen Motor
     hat: aktiv heisst mit Motor, passiv ohne. */
  const mech = useMemo(() => mechanisms(rig).find((m) => m.node === node.id) ?? null, [rig, node.id])
  const kids = childrenOf(rig, node.id)
  const path = chain(rig, node.id)
  const pos = world[node.id]

  const free = dof ? AXES.filter((a) => dof[a.key as AxisKey] !== null) : []

  /* Wie weit die Achse überhaupt fährt — der Bereich, über den eine
     Übersetzungskurve etwas aussagt. */
  const travel = free.reduce((max, a) => {
    const f = dof?.[a.key as AxisKey]
    return f ? Math.max(max, Math.abs(f.hi), Math.abs(f.lo)) : max
  }, 0)

  return (
    <Card
      title={node.label}
      eyebrow={`${KIND_LABEL[node.kind]}${mech && !mech.driven ? ', passiv' : ''} · ${STATE_LABEL[node.state]}${node.mirrored ? (asym ? ' · links und rechts verschieden' : ' · links wie rechts') : ''}${off ? ' · stillgelegt' : ''}`}
      actions={
        node.doc ? (
          <Link className="btn" data-variant="secondary" data-size="sm" to={`/${node.doc}`}>
            Ausarbeitung
          </Link>
        ) : undefined
      }
    >
      <div className="axis-detail">
        <div className="axis-detail__col">
          <span className="eyebrow">Lage</span>
          <table className="axis-detail__table">
            <tbody>
              <tr>
                <th scope="row">hängt an</th>
                <td>{node.parent ? model.points[node.parent as PointKey].label : 'Wurzel'}</td>
              </tr>
              <tr>
                <th scope="row">Abstand dorthin</th>
                <td className="numeric">
                  {node.parent ? `${num(branchLength(rig, node.id), 0)} mm` : '—'}
                  {node.atLeft && (
                    <span className="cell-muted">
                      {' '}
                      · links {num(Math.hypot(node.atLeft.x, node.atLeft.y, node.atLeft.z), 0)} mm
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">relativ</th>
                <td className="numeric">
                  {node.parent
                    ? `${num(node.at.x, 0)} · ${num(node.at.y, 0)} · ${num(node.at.z, 0)}`
                    : '—'}
                </td>
              </tr>
              <tr>
                <th scope="row">absolut</th>
                <td className="numeric">
                  {num(pos.x, 0)} · {num(pos.y, 0)} · {num(pos.z, 0)}
                </td>
              </tr>
              <tr>
                <th scope="row">Kette</th>
                <td>
                  <span className="cell-muted">
                    {path.map((id) => model.points[id as PointKey].label).join(' → ')}
                  </span>
                </td>
              </tr>
              <tr>
                <th scope="row">trägt</th>
                <td>
                  {kids.length === 0 ? (
                    <span className="cell-muted">nichts</span>
                  ) : (
                    kids.map((k) => k.label).join(', ')
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">Gangkurve</th>
                <td>
                  {node.role ?? <span className="cell-muted">keine aufgezeichnete Kurve</span>}
                </td>
              </tr>
              {mech && (
                <tr>
                  <th scope="row">Baugruppe</th>
                  <td>
                    {MECHANISM_LABELS[mech.kind]}
                    {mech.driven ? (
                      <span className="cell-muted">
                        {' '}
                        · {mech.motors} {mech.motors === 1 ? 'Motor' : 'Motoren'}
                      </span>
                    ) : (
                      <>
                        {' '}
                        <Badge tone="neutral">passiv</Badge>
                      </>
                    )}
                  </td>
                </tr>
              )}
              {mech && mech.driven && (
                <tr>
                  <th scope="row">Übersetzung</th>
                  <td className="numeric">
                    {ratioLabel(mech.ratio)}
                    {/* Eine Kurve nennt ihre Spanne; damit die Zahl daneben
                        nicht als zweite Wahrheit missverstanden wird, steht der
                        Nennwert als Mittelwert daran und nicht als eigener
                        Eintrag. */}
                    {mech.ratio.kind === 'kurve' && (
                      <span className="cell-muted"> · Mittel {num(mech.nominal, 1)}</span>
                    )}
                    {mech.ratio.kind === 'matrix' && (
                      <span className="cell-muted"> · beide Motoren auf beide Achsen</span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="axis-detail__col">
          <span className="eyebrow">
            Koordinaten · mm{asym ? ' · rechts und links' : ''}
          </span>
          <div className="axis-detail__fields">
            {asym && (
              <div className="axis-detail__field axis-detail__heads">
                <span />
                <span>rechts</span>
                <span>links</span>
              </div>
            )}
            {(['x', 'y', 'z'] as const).map((axis) => (
              <label key={axis} className="axis-detail__field">
                <span>{axis}</span>
                <NumberCell
                  value={point[axis]}
                  base={base.points[key][axis]}
                  min={POINT_RANGE.min}
                  max={POINT_RANGE.max}
                  step={POINT_RANGE.step}
                  digits={POINT_RANGE.digits}
                  readOnly={point.fixed}
                  onChange={(next) => editor.change(key, axis, next)}
                />
                {/* Die zweite Spalte gibt es erst, wenn jemand die Unsymmetrie
                    ausdrücklich gewollt hat. Vorher wäre sie ein Feld, das
                    dieselbe Zahl ein zweites Mal zeigt. */}
                {asym && (
                  <NumberCell
                    value={pointFor(model, key, 'L')[axis]}
                    base={point[axis]}
                    min={POINT_RANGE.min}
                    max={POINT_RANGE.max}
                    step={POINT_RANGE.step}
                    digits={POINT_RANGE.digits}
                    onChange={(next) => editor.changeLeft(key, axis, next)}
                  />
                )}
              </label>
            ))}
          </div>
          {asym && (
            <>
              <p className="metric__note">
                <Badge tone="warning">unsymmetrisch</Badge> Abweichend ist {own.join(', ')}. Die
                übrigen Achsen erbt die linke Seite weiter.
              </p>
              <Button size="sm" variant="ghost" onClick={() => editor.makeSymmetric(key)}>
                Unsymmetrie aufheben
              </Button>
            </>
          )}
          {!asym && node.mirrored && !point.fixed && (
            <p className="metric__note">
              Diesen Punkt gibt es links wie rechts, und beide tragen dieselbe Zahl. Beim Ändern
              wird gefragt, ob das so bleiben soll.
            </p>
          )}
          {point.fixed && (
            <p className="metric__note">
              Der Nullpunkt liegt fest; alle anderen Koordinaten sind auf ihn bezogen.
            </p>
          )}
        </div>

        <div className="axis-detail__col">
          <span className="eyebrow">Winkelfreiheiten</span>
          {free.length === 0 ? (
            <p className="metric__note">
              Keine Freiheit. Der Punkt bewegt sich nicht gegen seinen Elternknoten und braucht
              deshalb keinen Antrieb.
            </p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Achse</Th>
                  <Th numeric>von</Th>
                  <Th numeric>bis</Th>
                  <Th numeric>Moment</Th>
                </tr>
              </thead>
              <tbody>
                {free.map((axis) => {
                  const freedom = dof![axis.key as AxisKey]!
                  return (
                    <tr key={axis.key}>
                      <Td>
                        {axis.label} <span className="cell-muted">{axis.about}</span>
                      </Td>
                      {(['lo', 'hi', 'moment'] as const).map((field) => (
                        <Td key={field} numeric>
                          <NumberCell
                            value={freedom[field]}
                            base={base.dofs[dofKey!][axis.key as AxisKey]![field]}
                            min={DOF_RANGES[field].min}
                            max={DOF_RANGES[field].max}
                            step={DOF_RANGES[field].step}
                            unit={DOF_RANGES[field].unit}
                            digits={DOF_RANGES[field].digits}
                            onChange={(next) =>
                              setEdit(dofEdit(dofKey!, axis.key as AxisKey, field), next)
                            }
                          />
                        </Td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
          {mech && !mech.driven && free.length > 0 && (
            <p className="metric__note">
              <Badge tone="neutral">ohne Motor</Badge> Die Achse zählt in der Achszahl und nicht in
              der Motorzahl. Was sie hält, hält eine Feder — Kennlinie und Auslegung stehen unter
              Antriebe.
            </p>
          )}

          {node.mirrored && free.length > 0 && (
            <p className="metric__note">
              <Badge tone="neutral">×2</Badge> Diesen Punkt gibt es links wie rechts. Jede Freiheit
              zählt deshalb doppelt in der Achszahl und braucht ihren eigenen Antrieb.
            </p>
          )}

          {/*
            AUSSCHALTEN, NICHT LÖSCHEN. Wer wissen will, was eine Achse
            beiträgt, nimmt sie heraus und schaut. Was dabei passiert, steht
            unter dem Schalter — und es ist nicht wenig: der Winkel steht fest,
            der Bodenkontakt rechnet neu, die Achse fällt aus den Grenzen, und
            je nach zweitem Schalter fällt auch ihr Antrieb aus Masse und
            Kosten.
          */}
          {free.length > 0 && dofKey && (
            <div className="axis-detail__off">
              <Switch
                label="Achse stilllegen"
                checked={off !== null}
                onChange={(e) =>
                  setOff(dofKey, e.currentTarget.checked ? { at: 0, drive: false } : null)
                }
              />
              {off && (
                <>
                  <label className="axis-detail__field">
                    <span>Rastwinkel</span>
                    <NumberCell
                      value={off.at}
                      base={0}
                      min={-180}
                      max={180}
                      step={1}
                      unit="°"
                      digits={0}
                      onChange={(next) => setOff(dofKey, { ...off, at: next ?? 0 })}
                    />
                  </label>
                  <Switch
                    label="Antrieb bleibt verbaut"
                    checked={off.drive}
                    onChange={(e) => setOff(dofKey, { ...off, drive: e.currentTarget.checked })}
                  />
                  <p className="metric__note">
                    {off.drive
                      ? 'Die Achse ist gesperrt, der Antrieb bleibt verbaut und wiegt. Er hält nur noch.'
                      : 'Der Antrieb ist ausgebaut, das Moment trägt die Struktur. Masse, Kosten und Leistungsbedarf fallen weg.'}
                  </p>
                  <p className="metric__note">
                    Der Gang wird nicht neu geplant. Kadenz und Schrittlänge bleiben, der Winkel
                    wird danach festgehalten; was das am Boden anrichtet, meldet der Bodenkontakt.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/*
        DIE ÜBERSETZUNG ALS KURVE, wo sie eine ist.

        Unter den drei Spalten und nicht in einer davon: ein Diagramm über
        hundertvierzig Grad braucht Breite, und in der schmalsten Spalte wäre die
        Kurve ein Strich mit einem Ausschlag am Ende.

        Eine Kurbelschwinge hat an jedem Gelenkwinkel eine andere Übersetzung.
        Als Mittelwert geführt rechnet man an beiden Enden des Bereichs falsch —
        und sieht es nicht, weil eine Zahl keine Spanne hat. Das Bild hat eine.
      */}
      {mech?.ratio.kind === 'kurve' && <RatioCurve ratio={mech.ratio} limit={travel} />}

      {/* Was auf diesem Knoten SITZT — nicht, was an ihn anschliesst. */}
      <CarriedParts model={model} skeleton={skeleton} node={node.id} />
    </Card>
  )
}

/**
 * Die Übersetzung über dem Gelenkwinkel.
 *
 * Aus derselben Funktion wie die Zahl in der Tabelle — zwei Wege zu einer Zahl
 * wären zwei Wahrheiten, sobald jemand an der Geometrie dreht.
 */
function RatioCurve({ ratio, limit }: { ratio: Ratio; limit: number }) {
  const { values, to, breaks } = useMemo(() => {
    if (ratio.kind !== 'kurve') return { values: new Float32Array(0), to: 0, breaks: Infinity }
    const track = trackFor(ratio.linkage)
    /*
      Bis zum Anschlag der Achse und höchstens bis zur Totlage.

      NICHT BIS ZUR TOTLAGE, WENN DIE ACHSE VORHER ENDET: dort wächst die
      Übersetzung ins Unbeschränkte, und über den ganzen Bereich gezeichnet wird
      aus dem Verlauf im Gehbereich ein flacher Strich mit einem Ausschlag am
      Rand. Der Verlauf, der zählt, ist der bis zum Anschlag.
    */
    const dead = Number.isFinite(track.breaks) ? track.breaks : track.to
    const end = Math.min(track.to, dead, limit > 0 ? limit : dead)
    const n = 120
    const out = new Float32Array(n + 1)
    for (let k = 0; k <= n; k++) out[k] = ratioAt(ratio, (k / n) * end)
    return { values: out, to: end, breaks: track.breaks }
  }, [ratio])

  if (values.length === 0) return null

  return (
    <div className="axis-detail__curve">
      <CycleChart
        title="Übersetzung"
        unit=": 1"
        yLabel="Motor je Gelenk"
        xDomain={[0, Math.round(to)]}
        xUnit="°"
        xName="den Gelenkwinkel"
        xLabel="Gelenkwinkel"
        height={190}
        zero={false}
        foot={false}
        series={[{ key: 'i', label: 'Übersetzung', values, tone: 1 }]}
      />
      <p className="metric__note">
        Gerechnet aus der Viergelenkgeometrie, nicht eingetragen. Die Kette bricht bei{' '}
        {Number.isFinite(breaks) ? `${Math.round(breaks)}°` : 'keinem Winkel des Bereichs'} ein —
        dort steht die Kurbel in der Verlängerung der Schubstange.
      </p>
    </div>
  )
}

/**
 * Die Bauteile, die auf einem Knoten sitzen — jedes in seinem eigenen Rahmen.
 *
 * WARUM JEDES FÜR SICH UND NICHT ALLE IN EINEM BILD. Der Rahmen eines Bauteils
 * ist der seines Trägers, und der Ursprung liegt an seiner Aufhängung. Legt man
 * zwei Teile in ein Bild, braucht man wieder eine Pose, um sie
 * gegeneinanderzustellen — und dann zeigt das Bild die Stellung und nicht das
 * Teil. Nebeneinander gezeichnet zeigt jedes seine Form, seine Länge und seine
 * Dicke, und mehr will diese Ansicht nicht.
 */
function CarriedParts({
  model,
  skeleton,
  node,
}: {
  model: HumanoidModel
  skeleton: Skeleton
  node: string
}) {
  const carried = useMemo(() => partsOn(parts(model, skeleton), node), [model, skeleton, node])
  if (carried.length === 0) return null

  return (
    <div className="parts">
      <span className="eyebrow">Bauteile auf diesem Knoten</span>
      <div className="parts__row">
        {carried.map((part) => (
          <figure key={part.key} className="part">
            <PartShape part={part} />
            <figcaption>
              <b>{part.label}</b>
              <span className="part__facts numeric">
                {num(part.length * 1000, 0)} × ⌀ {num(2000 * part.radius, 0)} mm
              </span>
              <span className="part__from">
                {PROVENANCE_LABELS[part.provenance]}
                {part.mass !== null && <> · {num(part.mass, 2)} kg</>}
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="metric__note">
        Der Umriss kommt aus Länge und Dicke. Sobald aus der Konstruktion eine Hüllkurve
        zurückkommt, tritt sie an dieselbe Stelle — die Herkunft steht dann an diesem Teil und
        nicht in einer zweiten Liste.
      </p>
    </div>
  )
}

/**
 * Der Umriss eines Bauteils.
 *
 * Der Zeichenbereich folgt dem Teil und nicht umgekehrt: ein kurzes dickes Teil
 * und ein langes dünnes bekommen denselben Rand und damit vergleichbare
 * Proportionen. Ein fester Ausschnitt zeigte den Fuss als Fleck und den
 * Oberschenkel als Strich.
 */
function PartShape({ part }: { part: Part }) {
  /*
    GEZEICHNET WIRD IN MILLIMETERN, gerechnet in Metern.

    Der Rechenkern führt alles in Metern; ein Oberschenkel ist dort 0,42 lang
    und 0,06 dick. Eine Zeichnung mit solchen Zahlen verträgt keine
    Mindestgrösse — „mindestens 2" ist in einem Bild von 0,16 Breite das
    Zwölffache des ganzen Ausschnitts. Genau so ist der erste Entwurf zu zwei
    Kreisen geworden. Der Massstab steht deshalb HIER und einmal.
  */
  const mm = 1000
  const r = part.radius * mm
  const l = part.length * mm
  const pad = Math.max(6, r * 0.4)
  const w = 2 * r + 2 * pad
  const h = l + 2 * r + 2 * pad
  const dot = Math.max(1.5, r * 0.16)
  return (
    <svg
      className="part__svg"
      viewBox={`${-w / 2} ${-r - pad} ${w} ${h}`}
      role="img"
      aria-label={`Umriss ${part.label}`}
    >
      <path
        className="part__hull"
        d={outlinePath({ ...part, radius: r, length: l })}
      />
      {/* Die Achse und die beiden Anschlusspunkte: ohne sie sieht man die Form,
          aber nicht, wo das Teil hängt. */}
      <line className="part__axis" x1={0} y1={0} x2={0} y2={l} />
      <circle className="part__joint" cx={0} cy={0} r={dot} />
      <circle className="part__joint" cx={0} cy={l} r={dot} />
    </svg>
  )
}
